import type { ReactElement, ReactNode } from "react";
import {
	Box,
	Chip,
	CircularProgress,
	Divider,
	IconButton,
	Paper,
	Tab,
	Tabs,
	Tooltip,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const PANEL_BORDER = "#dde1ea";

interface DetailHeaderProps {
	title: string;
	active?: boolean;
	isSaving?: boolean;
	actions?: ReactNode;
	onClose: () => void;
}

export const DetailHeader = ({
	title,
	active,
	isSaving = false,
	actions,
	onClose,
}: DetailHeaderProps) => (
	<>
		<Box
			sx={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				gap: 1,
				// The title starts where the content of the sections below starts: body padding,
				// plus the section border and padding.
				pl: "27px",
				pr: 2,
				py: 1.25,
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
				<Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
					{title}
				</Typography>
				{active !== undefined && (
					<Chip
						label={active ? "Active" : "Inactive"}
						size="small"
						sx={{
							height: 20,
							fontSize: "0.7rem",
							fontWeight: 600,
							bgcolor: active ? "#e7f6ee" : "#eceff3",
							color: active ? "#1b7a4b" : "text.secondary",
						}}
					/>
				)}
				{isSaving && <CircularProgress size={16} />}
			</Box>
			<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
				{actions}
				<Tooltip title="Close">
					<IconButton size="small" onClick={onClose} disabled={isSaving}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>
		</Box>
		<Divider />
	</>
);

/** Everything under the header: the summary and the tabs, filling the panel. */
export const DetailBody = ({ children }: { children: ReactNode }) => (
	<Box
		sx={{
			flex: 1,
			minHeight: 0,
			display: "flex",
			flexDirection: "column",
			px: 2,
			pt: 1.25,
			pb: 0.75,
		}}
	>
		{children}
	</Box>
);

export interface DetailSummaryItem {
	label: string;
	value: ReactNode;
	/** Share of the strip: 1 is a plain value, more for one that runs long. */
	grow?: number;
}

/** Key facts that stay visible whatever tab is open, one cell each. */
export const DetailSummary = ({ items }: { items: DetailSummaryItem[] }) => (
	<Box
		sx={{
			display: "flex",
			flexWrap: "wrap",
			border: `1px solid ${PANEL_BORDER}`,
			borderRadius: 2,
			overflow: "hidden",
			mb: 1.25,
		}}
	>
		{items.map((item, index) => (
			<Box
				key={item.label}
				sx={{
					flex: `${item.grow ?? 1} 1 0`,
					minWidth: 96,
					px: 2,
					py: 0.75,
					borderLeft: index === 0 ? 0 : `1px solid ${PANEL_BORDER}`,
				}}
			>
				<Typography
					noWrap
					sx={{
						fontSize: "0.72rem",
						color: "text.secondary",
						lineHeight: 1.4,
					}}
				>
					{item.label}
				</Typography>
				<Typography
					noWrap
					title={typeof item.value === "string" ? item.value : undefined}
					sx={{
						fontSize: "0.875rem",
						fontWeight: 600,
						fontVariantNumeric: "tabular-nums",
					}}
				>
					{item.value === null || item.value === undefined || item.value === ""
						? "-"
						: item.value}
				</Typography>
			</Box>
		))}
	</Box>
);

export interface DetailTab<T extends string> {
	value: T;
	label: string;
	icon?: ReactElement;
	disabled?: boolean;
}

interface DetailTabsProps<T extends string> {
	tabs: DetailTab<T>[];
	value: T;
	onChange: (value: T) => void;
	/** Actions of the open tab, on the right of the tab strip. */
	actions?: ReactNode;
	/** The open tab is a single grid that runs edge to edge. */
	flush?: boolean;
	children: ReactNode;
}

/**
 * Underlined tabs over a panel that fills the remaining height. The panel is the only
 * scroll in the detail, and only when its content does not fit.
 */
export const DetailTabs = <T extends string>({
	tabs,
	value,
	onChange,
	actions,
	flush = false,
	children,
}: DetailTabsProps<T>) => (
	<Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				borderBottom: 1,
				borderColor: "divider",
			}}
		>
			<Tabs
				value={value}
				onChange={(_, next: T) => onChange(next)}
				sx={{
					flex: 1,
					minHeight: 36,
					"& .MuiTabs-indicator": { height: 3 },
					"& .MuiTabs-flexContainer": { gap: 0.5 },
				}}
			>
				{tabs.map((tab) => (
					<Tab
						key={tab.value}
						value={tab.value}
						label={tab.label}
						icon={tab.icon}
						iconPosition="start"
						disabled={tab.disabled}
						sx={{
							minHeight: 36,
							py: 0.25,
							px: 2,
							textTransform: "none",
							fontSize: "0.9rem",
							fontWeight: 600,
							color: "text.secondary",
							// An unselected tab keeps a light fill so it still reads as a tab you can open.
							bgcolor: "#f4f5f8",
							borderRadius: "8px 8px 0 0",
							"& .MuiTab-icon": { fontSize: 18, mr: 0.75 },
							"&:hover": { bgcolor: "#e9ecf2", color: "text.primary" },
							"&.Mui-selected": {
								color: "primary.main",
								bgcolor: "#e8ebfa",
							},
						}}
					/>
				))}
			</Tabs>
			{actions && (
				<Box sx={{ pb: 0.5, display: "flex", gap: 1 }}>{actions}</Box>
			)}
		</Box>
		<Box
			role="tabpanel"
			sx={{
				flex: 1,
				minHeight: 0,
				overflow: flush ? "hidden" : "auto",
				pt: 0.75,
				display: "flex",
				flexDirection: "column",
				gap: 0.75,
			}}
		>
			{children}
		</Box>
	</Box>
);

interface DetailSectionProps {
	title: string;
	icon?: ReactElement;
	actions?: ReactNode;
	/** Takes the height left in the tab, for a grid that should fill it. */
	fill?: boolean;
	children: ReactNode;
}

export const DetailSection = ({
	title,
	icon,
	actions,
	fill = false,
	children,
}: DetailSectionProps) => (
	<Paper
		variant="outlined"
		sx={{
			p: 1.25,
			pt: 0.75,
			borderColor: PANEL_BORDER,
			borderRadius: 2,
			flexShrink: 0,
			...(fill && {
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
			}),
		}}
	>
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				minHeight: 28,
				mb: 0.75,
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 0.75,
					color: "primary.main",
					"& svg": { fontSize: 18 },
				}}
			>
				{icon}
				<Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
					{title}
				</Typography>
			</Box>
			{actions}
		</Box>
		{fill ? <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box> : children}
	</Paper>
);
